<script setup lang="ts">
import {UiIcon, UiTooltip} from '@common/ui-kit';
import IconQuestion from "~/components/sx/icons/hero/IconQuestion.vue";
import StreamSelect, {type IStream} from "~/components/sx/prices/StreamSelect.vue";
import {capitalize} from "vue";

export interface IUnlimTariff {
  id: number,
  price: string,
  proxies_count: number,
  streams: IStream[],
}

const props = defineProps<{ item: IUnlimTariff, tag?: string }>();

const { queryRefParams } = useHttpReferrer();
const config = useRuntimeConfig();
const { t } = useI18n();

const visibleStreams = ref(false);
const parentRef = ref<HTMLElement | null>(null);
const position = ref({ top: 0, left: 0 });

const activeData = ref<IStream>(props.item.streams?.[0]);

const selectStream = (item: IStream) => {
  activeData.value = item;
};

const calculatePosition = () => {
  if (parentRef.value) {
    const rect = parentRef.value.getBoundingClientRect();
    position.value = {
      top: rect.bottom + window.scrollY - 12,
      left: rect.left + window.scrollX - 12,
    };
  }
};

const toggleStreams = async () => {
  visibleStreams.value = !visibleStreams.value;
  if (visibleStreams.value) {
    await nextTick();
    calculatePosition();
  }
};

const onResizeOrScroll = () => {
  if (visibleStreams.value) {
    calculatePosition();
  }
};

const toggleVisibility = () => {
  visibleStreams.value = false;
};

function formatSmart(num: number) {
  const fixed = num.toFixed(2);
  return fixed.endsWith('0') ? num.toFixed(1) : fixed;
}

onMounted(() => {
  window.addEventListener('resize', onResizeOrScroll);
  window.addEventListener('scroll', onResizeOrScroll, true);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResizeOrScroll);
  window.removeEventListener('scroll', onResizeOrScroll, true);
});
</script>

<template>
  <div class="prices-unlim-item">
    <div v-if="tag" class="prices-unlim-item__tag">
      {{ tag }}
    </div>
    <div class="prices-unlim-item__inner">
      <div class="prices-unlim-item__block price">
        <div class="prices-unlim-item__value price">
          ${{ activeData?.price }}
          <div class="sub">
            / {{ $t('pages.home.prices.common.month') }}
          </div>
        </div>
      </div>
      <div class="prices-unlim-item__block count">
        <div class="prices-unlim-item__title">
          {{ capitalize($t('pages.home.prices.unlimItem.proxyCountFull')) }}
        </div>
        <div class="prices-unlim-item__divider" />
        <div class="prices-unlim-item__value">
          {{ item.proxies_count }}
        </div>
      </div>
      <div class="prices-unlim-item__block pricePerProxy"  style="min-width: 162px">
        <div class="prices-unlim-item__title">
          {{ capitalize($t('pages.home.prices.unlimItem.pricePerProxy')) }}
        </div>
        <div class="prices-unlim-item__divider" />
        <div class="prices-unlim-item__value">
          ${{ formatSmart(activeData?.price / item.proxies_count) }} {{ $t('pages.home.prices.common.perMonth') }}
        </div>
      </div>
      <div class="prices-unlim-item__block" style="min-width: 144px">
        <div class="prices-unlim-item__title">
          {{ capitalize($t('pages.home.prices.unlimItem.limitation')) }}
        </div>
        <div class="prices-unlim-item__divider" />
        <div class="prices-unlim-item__value green">
          {{ capitalize($t('pages.home.prices.unlimItem.unlimited')) }}
        </div>
      </div>
      <div v-if="false" class="prices-unlim-item__block" style="min-width: 184px">
        <div class="prices-unlim-item__title">
          {{ capitalize($t('pages.home.prices.unlimItem.connectionsFull')) }}
          <UiTooltip
            popperClass="text"
            position="bottom"
            mode="dark"
            :title="$t('pages.home.prices.unlimItem.tooltips.stream.title')"
          >
            <div style="color: #fff">
              <IconQuestion />
            </div>
            <template #text>
              <div class="prices-unlim-item__tooltip">
                {{ capitalize($t('pages.home.prices.unlimItem.tooltips.stream.text')) }}
              </div>
            </template>
          </UiTooltip>
        </div>
        <div class="prices-unlim-item__divider" />
        <div
          ref="parentRef"
          class="prices-unlim-item__value relative"
          :class="{ dashed: item.streams?.length }"
          @click="toggleStreams"
        >
          <div class="prices-unlim-item__value-inner">
            {{ activeData?.quantity }} {{ $t('pages.home.prices.unlimItem.streams') }}
            <UiIcon
              type="400"
              name="chevron-left 1"
              class="prices-unlim-item__value-chevron"
              :class="{ active: visibleStreams }"
            />
          </div>
          <Teleport to="body">
            <div
              v-if="item.streams?.length && visibleStreams"
              class="prices-unlim-item__streams-popup"
              :style="`top: ${position.top}px; left: ${position.left}px;`"
            >
              <StreamSelect
                :streams="item.streams"
                :active-id="activeData.id"
                class="prices-unlim-item__streams"
                @on-select="selectStream"
                @toggle-visibility="toggleVisibility"
              />
            </div>
          </Teleport>
        </div>
      </div>
      <div class="prices-unlim-item__block">
        <UiTooltip
          mode="dark"
          position="bottom"
          :title="$t('pages.home.prices.unlimItem.tooltips.pack.title')"
        >
          <div class="prices-unlim-item__value dashed">
            {{ capitalize($t('pages.home.prices.unlimItem.whatIncludes')) }}
          </div>
          <template #text>
            <div class="prices-unlim-item__tooltip">
              <div class="prices-unlim-item__tooltip-block">
                <strong>
                  {{ $t('pages.home.prices.unlimItem.tooltips.pack.types.header') }}
                </strong>
                <div>
                  {{ $t('pages.home.prices.unlimItem.tooltips.pack.types.content') }}
                </div>
              </div>
              <div class="prices-unlim-item__tooltip-block">
                <strong>
                  {{ $t('pages.home.prices.unlimItem.tooltips.pack.geo.header') }}
                </strong>
                <div>
                  {{ $t('pages.home.prices.unlimItem.tooltips.pack.geo.content') }}
                </div>
              </div>
              <div class="prices-unlim-item__tooltip-block row">
                <strong>
                  {{ $t('pages.home.prices.unlimItem.tooltips.pack.restrictions.header') }}
                </strong>
                <span class="underline">
                  {{ $t('pages.home.prices.unlimItem.tooltips.pack.restrictions.content') }}
                </span>
              </div>
            </div>
          </template>
        </UiTooltip>
      </div>
      <div class="prices-unlim-item__block">
        <a
          class="prices-unlim-item__btn default-tag"
          :href="config.public.signupUrl + queryRefParams"
        >
          {{ capitalize($t('pages.home.prices.common.buy')) }}
        </a>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
:deep(.ui-tooltip__text) {
  color: red
}
.prices-unlim-item {
  border: var(--border-sx-main-opacity);
  background-color: var(--bg-sx-darkgray);
  border-radius: 24px;
  padding: 16px 24px;
  z-index: 9999;
  position: relative;
  @media (max-width: 767px) {
    width: 100%;
    padding: 1rem;
  }
  &__tag {
    position: absolute;
    top: 0;
    transform: translateY(-50%);
    background: var(--bg-sx-demiblack);
    display: flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    padding: 4px 12px;
    border-radius: 8px;
    border: var(--border-sx-main-opacity);
  }
  &__streams-popup {
    position: absolute;
    z-index: 9999;
  }
  &__tooltip {
    font-family: Roboto, sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
    &-block {
      display: flex;
      flex-direction: column;
      gap: 8px;
      &.row {
        flex-direction: row;
        gap: 4px;
      }
      .underline {
        text-decoration: underline;
      }
    }
  }
  &__inner {
    display: flex;
    align-items: center;
    gap: 32px;
    justify-content: space-between;
    @media (max-width: 1199px) {
      gap: 16px;
    }
  }
  &__block {
    display: flex;
    flex-direction: column;
    &.price {
      min-width: 140px;
    }
    &.count {
      min-width: 140px;
    }
    &.pricePerProxy {
      min-width: 180px !important;
    }
  }
  &__divider {
    margin-bottom: 1px;
    width: 100%;
    border-bottom: 1px solid rgba(224, 224, 224, 0.26);
  }
  &__title {
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0;
    color: var(--font-sx-color-contrast);
  }
  &__value {
    font-size: 24px;
    line-height: 32px;
    letter-spacing: 0;
    &.green {
      color: var(--font-sx-color-green)
    }
    &.price {
      font-weight: 700;
    }
    &-inner {
      display: flex;
      align-items: center;
      gap: 4px;
      padding-top: 2px;
    }
    &-chevron {
      transform: rotate(-90deg);
      transition: .3s;
      width: 24px;
      height: 24px;
      &.active {
        transform: rotate(-270deg);
      }
    }
    &.dashed {
      text-decoration: underline;
      text-decoration-style: dashed;
      text-underline-offset: 5px;
      cursor: pointer;
      &:hover {
        color: #dfdfdf;
      }
      @media (max-width: 1199px) {
        display: flex;
        justify-content: center;
      }
    }
    .relative {
      position: relative;
      z-index: 10000;
    }
    .sub {
      font-weight: 400;
      font-size: 22px;
      line-height: 22px;
      letter-spacing: -3%;
      vertical-align: middle;
    }
  }
  &__streams {
    position: absolute;
    top: 20px;
    background: #000;
    z-index: 1000;
  }
  &__btn {
    width: fit-content;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 56px;
    border-radius: 30px;
    margin-block-start: auto;
    font-size: 16px;
    line-height: 20px;
    color: #000;
    background: #fff;
    min-width: 164px;
    &:hover {
      background: #eeeeee;
    }
  }
}

@media (max-width: 1199px) {
  .prices-unlim-item {
    &__inner {
      flex-direction: column;
    }
    &__block {
      text-align: center;
      &.price {
        order: -1
      }

    }
  }
}
</style>